import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Box, Paper, Typography, Divider, TextField, useMediaQuery, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PercentIcon from '@mui/icons-material/Percent';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import axios, { portalRole } from '../services/api';
import { money } from '../services/swil';
import { SchemeRibbon, ProductFormIcon } from './OrderParts';
import './OrderPages.css';

const Cart = ({ cartItems, onRemoveFromCart, onUpdateCartItem, onClearCart, customer }) => {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's a mobile view

  const incrementQty = (product) => {
    onUpdateCartItem({ ...product, quantity: product.quantity + 1 });
  };

  const decrementQty = (product) => {
    if (product.quantity > 1) {
      onUpdateCartItem({ ...product, quantity: product.quantity - 1 });
    } else {
      onRemoveFromCart(product.product_id);
    }
  };

  const calculateTotal = (ptr, quantity, gst, scheme) => {
    const baseTotal = ptr * quantity;

    let schemeDiscount = 0;
    if (scheme && scheme.includes('%')) {
      const schemePercentage = parseFloat(scheme.replace('%', ''));
      if (!isNaN(schemePercentage)) {
        schemeDiscount = (baseTotal * schemePercentage) / 100;
      }
    }

    const discountedTotal = baseTotal - schemeDiscount;
    const gstAmount = (discountedTotal * gst) / 100;
    const finalTotal = discountedTotal + gstAmount;

    return {
      baseTotal: baseTotal.toFixed(2),
      schemeDiscount: schemeDiscount.toFixed(2),
      discountedTotal: discountedTotal.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      finalTotal: finalTotal.toFixed(2),
    };
  };

  const grandTotalDetails = cartItems.reduce(
    (acc, item) => {
      const { baseTotal, schemeDiscount, gstAmount, finalTotal } = calculateTotal(item.ptr, item.quantity, item.gst, item.scheme);
      acc.grossAmount += parseFloat(baseTotal);
      acc.discountedAmount += parseFloat(schemeDiscount);
      acc.gstAmount += parseFloat(gstAmount);
      acc.finalTotal += parseFloat(finalTotal);
      return acc;
    },
    { grossAmount: 0, discountedAmount: 0, gstAmount: 0, finalTotal: 0 }
  );

  const grandTotalFormatted = {
    grossAmount: grandTotalDetails.grossAmount.toFixed(2),
    discountedAmount: grandTotalDetails.discountedAmount.toFixed(2),
    gstAmount: grandTotalDetails.gstAmount.toFixed(2),
    finalTotal: grandTotalDetails.finalTotal.toFixed(2),
  };

  const handlePlaceOrder = () => {
    if (isPlacingOrder || !customer || !cartItems.length) return;
    setIsPlacingOrder(true); // Disable the button and show the loading icon
    const orderDetails = cartItems.map((item) => {
      const { schemeDiscount, gstAmount, finalTotal } = calculateTotal(item.ptr, item.quantity, item.gst, item.scheme);
      return {
        product_id: item.product_id,
        prod_id: item.product_id,
        prod_name_to_display: item.product_name,
        prod_conv1: '', // Assuming no value is needed for these fields
        prod_unit2: '',
        mkt_group: '',
        product_mrp: item.ptr, // PTR value
        qty: item.quantity,
        gst_code: gstAmount, // GST amount
        discount_percentage: item.scheme ? parseFloat(item.scheme.replace('%', '')) : 0, // Scheme percentage
        discount_amount: schemeDiscount, // Scheme discount amount
        final_amount: finalTotal, // Final total after scheme discount and GST
      };
    });

    const orderData = {
      customer_id: customer.customer_id,
      coupon_code: '', // Assuming no coupon is being used
      gross_amount: grandTotalFormatted.grossAmount, // Sum of base amounts
      discounted_amount: grandTotalFormatted.discountedAmount, // Total scheme discount amount
      coupon_discount: 0, // Total GST amount
      amount: grandTotalFormatted.finalTotal, // Final total amount
      customer: customer.id, // Customer's internal ID
      customer_address: customer.address,
      quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0), // Total quantity of all products
      order_details: orderDetails,
    };

    axios
      .post('place_order.php', orderData)
      .then((response) => {
        if (response.data.status !== 'success') throw new Error(response.data.message || 'Unable to place the order.');
        alert('Order placed successfully.');
        localStorage.removeItem('cartItems'); // Clear cart items from local storage
        onClearCart(); // Clear all items from the cart state
        navigate('/orders'); // Redirect to ViewOrder page
      })
      .catch((error) => {
        console.error('There was an error placing the order!', error);
        alert(error.message || 'Unable to place the order. Your cart has been kept.');
      })
      .finally(() => setIsPlacingOrder(false));
  };

  if (!customer) {
    return <div className="order-page"><div className="order-empty">{portalRole === 'salesman' ? <><strong>No customer selected</strong>Select a customer from My Dashboard first.</> : <><strong>Loading your account…</strong>Your cart will appear in a moment.</>}</div></div>;
  }

  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <section className="order-page cart-section" aria-label="Cart">
      <div className="cart-head">
        <div>
          <div className="order-eyebrow">Your cart</div>
          <h2>{portalRole === 'salesman' ? `Order for ${customer.customer_name}` : 'Review your order'}</h2>
        </div>
        <span className="cart-count">{cartItems.length ? `${cartItems.length} ${cartItems.length === 1 ? 'product' : 'products'} · ${itemCount} ${itemCount === 1 ? 'unit' : 'units'}` : 'No products yet'}</span>
      </div>
      {process.env.NODE_ENV === 'development' && <div className="cart-note"><InfoOutlined fontSize="small" />Local development: orders are saved locally and are not sent to SWIL.</div>}

      <div className="cart-layout">
        <div className="cart-items">
          {!cartItems.length && <div className="order-empty"><strong>Your cart is empty</strong>Search for a product above and add it to start an order.</div>}
          {cartItems.map((product) => {
            const {
              baseTotal,
              schemeDiscount,
              discountedTotal,
              gstAmount,
              finalTotal,
            } = calculateTotal(product.ptr, product.quantity, product.gst, product.scheme);

            return (
              <Paper
                style={{
                  overflow: 'hidden',
                  borderRadius: '10px',
                  transition: 'transform 0.3s',
                  border: '1px solid #d9e1e6',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                }}
                key={product.product_id}
              >
                <Box display="flex" flexDirection="column">
                  {/* Product Header */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="6px 12px"
                    style={{
                      backgroundColor: '#4a90e2',
                      padding: '0.5rem 1rem',
                    }}
                  >
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white', textAlign: 'left' }}>
                      {product.product_name}
                    </Typography>
                    <SchemeRibbon scheme={product.scheme} />
                  </Box>

                  {/* Quantity Control Section */}
                  <Box display="flex" alignItems="center" justifyContent="center" padding="0.75rem 1rem" position="relative">
                    <ProductFormIcon name={product.product_name} />
                    <IconButton
                      onClick={() => decrementQty(product)}
                      style={{ color: '#ff5722', transition: 'transform 0.3s' }}
                      className="decrement-button"
                    >
                      <RemoveIcon />
                    </IconButton>
                    <TextField
                      value={product.quantity}
                      style={{ width: '50px', margin: '0 10px' }}
                      inputProps={{ min: 0, style: { textAlign: 'center' }, readOnly: true }}
                      disabled
                    />
                    <IconButton
                      onClick={() => incrementQty(product)}
                      style={{ color: '#4caf50', transition: 'transform 0.3s' }}
                      className="increment-button"
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => onRemoveFromCart(product.product_id)}
                      style={{ color: '#f44336', marginLeft: '10px' }}
                      aria-label={`Remove ${product.product_name} from cart`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  {/* Calculation Breakdown Section */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap={isMobile ? 'wrap' : 'nowrap'}
                    padding="0.5rem 1rem"
                    style={{
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      margin: '0 0.5rem 0.5rem',
                      textAlign: 'center',
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <CurrencyRupeeIcon fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: '5px' }}>
                        ₹{baseTotal}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <LocalOfferIcon fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: '5px' }}>
                        - ₹{schemeDiscount}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: '5px' }}>
                        ₹{discountedTotal}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <PercentIcon fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: '5px' }}>
                        GST: ₹{gstAmount}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon fontSize="small" />
                      <Typography variant="body2" style={{ marginLeft: '5px', fontWeight: 'bold' }}>
                        ₹{finalTotal}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Product Details Footer */}
                  <Divider style={{ width: '100%' }} />
                  <Box
                    style={{
                      backgroundColor: '#f5f5f5',
                      padding: '0.5rem 1rem',
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        color: '#333',
                        textAlign: 'left',
                      }}
                    >
                      MRP: ₹{product.mrp} | PTR: ₹{product.ptr} | GST: {product.gst}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </div>

        <aside className="cart-summary" aria-label="Order summary">
          <h3>Order summary</h3>
          <dl>
            <div><dt>Gross amount</dt><dd>{money(grandTotalFormatted.grossAmount)}</dd></div>
            <div className="is-discount"><dt>Scheme discount</dt><dd>− {money(grandTotalFormatted.discountedAmount)}</dd></div>
            <div><dt>GST amount</dt><dd>{money(grandTotalFormatted.gstAmount)}</dd></div>
          </dl>
          <div className="cart-grand"><span>Grand total</span><strong>{money(grandTotalFormatted.finalTotal)}</strong></div>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePlaceOrder}
            disabled={isPlacingOrder || !cartItems.length || !customer}
            startIcon={isPlacingOrder ? <CircularProgress size={18} color="inherit" /> : <ShoppingCartCheckoutRounded />}
          >
            {isPlacingOrder ? 'Placing order…' : 'Place order'}
          </Button>
        </aside>
      </div>
    </section>
  );
};

export default Cart;
